import { useEffect, useState, useCallback, useRef } from "react";
import { downloadGeneratedDocument, addGeneratedFolder, deleteGeneratedFolder, updateGeneratedFolderName, updateGeneratedDocumentFolder, updateGeneratedDocumentName, deleteGeneratedDocument } from "../api/generatedDocuments";
import { Download, Trash2, Edit, X, Folder, FileText, FolderPlus, Clock } from "lucide-react";
import "../styles/AutomationList.css";
import { useAuth } from "../context/AuthContext";
import { Loader } from "./ui/loader";
import Toast from "./automation/Toast";
import { Button } from "./ui/button";
import { fetchGeneratedFolderById, GeneratedFolderTreeDto, GeneratedFileDto, searchGeneratedDocuments } from "../api/generatedDocuments";
import WordPreview from "./WordPreview";
import React from "react";
import ReactDOM from 'react-dom';

export default function GeneratedFiles() {
  const [searchQuery, setSearchQuery] = useState("");
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const { user, loading: authLoading } = useAuth();
  const tenantId = user?.tenantId;
  // Foldery
  const [currentNode, setCurrentNode] = useState<GeneratedFolderTreeDto | null>(null);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<{ id: string | null, name: string }[]>([{ id: null, name: 'Wszystkie dokumenty' }]);
  const [isFolderAndFilesLoading, setIsFolderAndFilesLoading] = useState(false);
  const [isAddFolderOpen, setIsAddFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [isDeletingFolder, setIsDeletingFolder] = useState<string | null>(null);
  const [isEditingFolder, setIsEditingFolder] = useState<GeneratedFolderTreeDto | null>(null);
  const [editFolderName, setEditFolderName] = useState("");
  const [isEditingFolderLoading, setIsEditingFolderLoading] = useState(false);
  const [editFolderError, setEditFolderError] = useState<string | null>(null);
  const [draggedFileId, setDraggedFileId] = useState<string | null>(null);
  const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null);
  // Nowe stany
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<GeneratedFileDto[]>([]);
  const [searchBreadcrumbs, setSearchBreadcrumbs] = useState<{ id: string | null, name: string }[]>([]);
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
  const [isEditingDocument, setIsEditingDocument] = useState<GeneratedFileDto | null>(null);
  const [editDocumentName, setEditDocumentName] = useState("");
  const [isEditingDocumentLoading, setIsEditingDocumentLoading] = useState(false);
  const [editDocumentError, setEditDocumentError] = useState<string | null>(null);
  const [isDeletingDocument, setIsDeletingDocument] = useState<GeneratedFileDto | null>(null);
  const [searchSuggestions, setSearchSuggestions] = useState<GeneratedFileDto[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const suppressSuggestionsRef = useRef(false);
  const [previewDocument, setPreviewDocument] = useState<GeneratedFileDto | null>(null);
  const iframeTimeoutRef = React.useRef<number | null>(null);

  // Pobieranie folderu (lub root) i breadcrumbs
  const loadFolder = useCallback(async (folderId: string | null) => {
    if (!tenantId) return;
    setIsFolderAndFilesLoading(true);
    try {
      let node: GeneratedFolderTreeDto | null = null;
      if (folderId === null) {
        const resp = await fetchGeneratedFolderById(tenantId, null);
        if (Array.isArray(resp)) {
          // Backend zwraca tablicę: musimy zbudować wirtualny root
          const folders = resp.filter((n: any) => n.id !== null);
          const rootNode = resp.find((n: any) => n.id === null);
          const documents = rootNode && Array.isArray(rootNode.documents) ? rootNode.documents : [];
          node = {
            id: null,
            name: 'Wszystkie dokumenty',
            tenantId: '',
            createdAt: '',
            parentId: null,
            children: folders,
            documents,
          };
        } else {
          node = resp;
        }
      } else {
        node = await fetchGeneratedFolderById(tenantId, folderId);
      }
      setCurrentNode(node);
      // Buduj breadcrumbs
      const crumbs: { id: string | null, name: string }[] = [];
      let current = node;
      while (current && current.id) {
        crumbs.unshift({ id: current.id, name: current.name || "" });
        if (current.parentId) {
          current = await fetchGeneratedFolderById(tenantId, current.parentId);
        } else {
          current = null;
        }
      }
      crumbs.unshift({ id: null, name: 'Wszystkie dokumenty' });
      setBreadcrumbs(crumbs);
    } catch (e) {
      setToast({ message: "Błąd ładowania folderu.", type: "error" });
    } finally {
      setIsFolderAndFilesLoading(false);
    }
  }, [tenantId]);

  // Ładuj root na start i po zmianie tenantId
  useEffect(() => { loadFolder(null); }, [tenantId]);
  // Ładuj folder po zmianie currentFolderId
  useEffect(() => { loadFolder(currentFolderId); }, [currentFolderId]);

  // Obsługa wejścia do folderu
  const handleEnterFolder = (folderId: string) => { setCurrentFolderId(folderId); setSearchQuery(""); setIsSearching(false); };
  // Obsługa kliknięcia w breadcrumb
  const handleBreadcrumbClick = (id: string | null) => {
    setCurrentFolderId(id);
    setSearchQuery("");
    setIsSearching(false);
    setSearchResults([]);
    setSearchBreadcrumbs([]);
  };

  // Dodawanie folderu
  const handleAddFolder = async (name: string) => {
    if (!tenantId) return;
    try {
      await addGeneratedFolder(tenantId, name, currentFolderId);
      setIsAddFolderOpen(false);
      setNewFolderName("");
      await loadFolder(currentFolderId);
      setToast({ message: "Folder został dodany!", type: "success" });
    } catch (e) {
      setToast({ message: e.message || "Błąd dodawania folderu.", type: "error" });
    }
  };
  // Usuwanie folderu
  const handleDeleteFolder = async (folderId: string) => {
    if (!tenantId) return;
    try {
      await deleteGeneratedFolder(tenantId, folderId);
      setIsDeletingFolder(null);
      await loadFolder(currentFolderId);
      setToast({ message: "Folder został usunięty!", type: "success" });
    } catch (e) {
      setToast({ message: e.message || "Błąd usuwania folderu.", type: "error" });
    }
  };
  // Edycja folderu
  const handleEditFolder = async () => {
    if (!tenantId || !isEditingFolder || !editFolderName) return;
    setIsEditingFolderLoading(true);
    setEditFolderError(null);
    try {
      await updateGeneratedFolderName(tenantId, isEditingFolder.id!, editFolderName);
      setIsEditingFolder(null);
      setEditFolderName("");
      await loadFolder(currentFolderId);
      setToast({ message: "Nazwa folderu została zmieniona!", type: "success" });
    } catch {
      setEditFolderError("Błąd zmiany nazwy folderu.");
    } finally {
      setIsEditingFolderLoading(false);
    }
  };
  // New search suggestions logic
  useEffect(() => {
    if (!tenantId || !searchQuery.trim()) {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      setIsSearching(false);
      setSearchResults([]);
      setSearchBreadcrumbs([]);
      return;
    }
    if (suppressSuggestionsRef.current) {
      suppressSuggestionsRef.current = false;
      return;
    }
    const timeout = setTimeout(async () => {
      const results = await searchGeneratedDocuments(tenantId, searchQuery.trim());
      setSearchSuggestions(results.slice(0, 5));
      setShowSuggestions(true);
    }, 200);
    return () => clearTimeout(timeout);
  }, [searchQuery, tenantId]);

  useEffect(() => {
    setSelectedSuggestionIndex(-1);
  }, [searchSuggestions, searchQuery, showSuggestions]);

  function handleSearchInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (showSuggestions && searchSuggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedSuggestionIndex(idx => Math.min(idx + 1, searchSuggestions.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedSuggestionIndex(idx => Math.max(idx - 1, 0));
        return;
      }
      if (e.key === 'Enter') {
        if (selectedSuggestionIndex >= 0 && selectedSuggestionIndex < searchSuggestions.length) {
          handleSuggestionClick(searchSuggestions[selectedSuggestionIndex]);
          return;
        }
        setShowSuggestions(false);
        return;
      }
      if (e.key === 'Escape') {
        setShowSuggestions(false);
        return;
      }
    }
  }

  // Helper do szukania ścieżki w drzewie folderów po id
  function findPath(node, targetId) {
    if (!node) return [];
    if (node.id === targetId) return [node];
    if (node.children && node.children.length > 0) {
      for (const child of node.children) {
        const childPath = findPath(child, targetId);
        if (childPath.length > 0) return [node, ...childPath];
      }
    }
    return [];
  }

  async function handleSuggestionClick(doc: GeneratedFileDto) {
    suppressSuggestionsRef.current = true;
    setShowSuggestions(false);
    setSearchQuery(doc.title);
    setIsSearching(true);
    setSearchResults([doc]);
    // Jeśli doc ma folderId, wyznacz breadcrumbs jako pełną ścieżkę do folderu + plik
    if (doc.folderId && tenantId) {
      let tree = await fetchGeneratedFolderById(tenantId, null);
      let rootNode = tree;
      if (Array.isArray(tree)) {
        // Zbuduj wirtualny root node jak w loadFolder
        const folders = tree.filter((n: any) => n.id !== null && n.parentId === null);
        const root = tree.find((n: any) => n.id === null);
        const documents = root && Array.isArray(root.documents) ? root.documents : [];
        rootNode = {
          id: null,
          name: 'Wszystkie dokumenty',
          tenantId: '',
          createdAt: '',
          parentId: null,
          children: folders,
          documents,
        };
      }
      // Poprawione: znajdź całą ścieżkę do folderu, w którym jest dokument
      let path = findPath(rootNode, doc.folderId);
      // Usuń root node jeśli jest duplikatem
      if (path.length > 0 && path[0].id === null) {
        path = path.slice(1);
      }
      // Zbuduj breadcrumbs: root + ścieżka do folderu + plik
      let crumbs = [{ id: null, name: 'Wszystkie dokumenty' }];
      crumbs = crumbs.concat(path.map(f => ({ id: f.id, name: f.name })));
      crumbs.push({ id: doc.id, name: doc.title });
      setSearchBreadcrumbs(crumbs);
    } else {
      setSearchBreadcrumbs([{ id: null, name: 'Wyniki wyszukiwania' }, { id: doc.id, name: doc.title }]);
    }
  }

  async function handleShowAllResults() {
    if (!tenantId || !searchQuery.trim()) return;
    setIsSearching(true);
    setShowSuggestions(false);
    const results = await searchGeneratedDocuments(tenantId, searchQuery.trim());
    setSearchResults(results);
    setSearchBreadcrumbs([{ id: null, name: 'Wyniki wyszukiwania' }]);
  }

  // Renderowanie: analogicznie do AutomationList
  const folders = currentNode && Array.isArray(currentNode.children) ? currentNode.children : [];
  let documents = currentNode && Array.isArray(currentNode.documents) ? currentNode.documents : [];
  // W root pokazuj TYLKO dokumenty, które mają folderId === null
  if (currentNode && currentNode.id === null && Array.isArray(currentNode.documents)) {
    documents = currentNode.documents.filter(doc => doc.folderId === null);
  }

  // TODO: Tymczasowe sortowanie po dacie modyfikacji (do usunięcia po wdrożeniu sortowania na backendzie)
  const sortedFolders = [...folders].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  const sortedDocuments = [...documents].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  if (authLoading) return <div>Ładowanie...</div>;
  if (!tenantId) return <div>Błąd: Brak tenantId</div>;

  const handleEditDocument = async () => {
    if (!tenantId || !isEditingDocument || !editDocumentName) return;
    setIsEditingDocumentLoading(true);
    setEditDocumentError(null);
    try {
      // Jeśli folderId jest null lub undefined, użyj aktualnej lokalizacji (currentNode?.id)
      let folderIdToSend = isEditingDocument.folderId;
      if (folderIdToSend === null || folderIdToSend === undefined) {
        folderIdToSend = currentNode ? currentNode.id : null;
      }
      await updateGeneratedDocumentName(
        tenantId,
        isEditingDocument.id,
        folderIdToSend,
        editDocumentName
      );
      setIsEditingDocument(null);
      setEditDocumentName("");
      // Jeśli jesteś w trybie wyszukiwania, zaktualizuj searchQuery na nową nazwę
      if (isSearching && searchQuery.trim()) {
        setSearchQuery(editDocumentName);
        const results = await searchGeneratedDocuments(tenantId, editDocumentName);
        setSearchResults(results);
      }
      await loadFolder(currentFolderId);
      setToast({ message: "Nazwa dokumentu została zmieniona!", type: "success" });
    } catch {
      setEditDocumentError("Błąd zmiany nazwy dokumentu.");
    } finally {
      setIsEditingDocumentLoading(false);
    }
  };

  const handleDeleteDocument = async () => {
    if (!tenantId || !isDeletingDocument) return;
    try {
      await deleteGeneratedDocument(tenantId, isDeletingDocument.id);
      setIsDeletingDocument(null);
      await loadFolder(currentFolderId);
      setToast({ message: "Dokument został usunięty!", type: "success" });
    } catch {
      setToast({ message: "Błąd usuwania dokumentu.", type: "error" });
    }
  };

  function formatDateTime(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const day = date.toLocaleDateString('pl-PL');
    const time = date.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
    const full = date.toLocaleString('pl-PL');
    const isToday = date.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    const isYesterday = date.toDateString() === yesterday.toDateString();
    let label = day;
    if (isToday) label = 'Dziś';
    else if (isYesterday) label = 'Wczoraj';
    return { label, time, full };
  }

  return (
    <>
      {/* Header + Breadcrumbs */}
      {!isSearching || !searchQuery.trim() ? (
        <div className="automation-header enhanced-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', background: 'linear-gradient(90deg, #e0e7ff 0%, #f8fafc 100%)', borderRadius: 18, boxShadow: '0 2px 16px rgba(37,99,235,0.07)', padding: '18px 32px 18px 24px', minHeight: 64 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 0, marginTop: 0 }}>
            {breadcrumbs.map((b, i, arr) => (
              <span
                key={b.id || 'root'}
                style={{
                  color: i === arr.length - 1 ? '#2563eb' : '#888',
                  cursor: 'pointer',
                  fontWeight: i === arr.length - 1 ? 700 : 500,
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: 20,
                  background: dragOverFolderId === (b.id ?? 'root') ? '#e0e7ff' : 'transparent',
                  borderRadius: dragOverFolderId === (b.id ?? 'root') ? 10 : 0,
                  padding: dragOverFolderId === (b.id ?? 'root') ? '4px 12px' : 0,
                  transition: 'background 0.18s',
                  boxShadow: dragOverFolderId === (b.id ?? 'root') ? '0 2px 8px #c7d2fe' : 'none',
                }}
                onClick={() => (i !== arr.length - 1 ? handleBreadcrumbClick(b.id) : undefined)}
                onDragOver={e => { e.preventDefault(); setDragOverFolderId(b.id ?? 'root'); }}
                onDragLeave={e => { e.preventDefault(); setDragOverFolderId(null); }}
                onDrop={async e => {
                  e.preventDefault();
                  setDragOverFolderId(null);
                  if (draggedFileId && tenantId) {
                    await updateGeneratedDocumentFolder(tenantId, draggedFileId, b.id || null);
                    setDraggedFileId(null);
                    if ((b.id || null) === null) {
                      setIsSearching(false);
                      setSearchQuery("");
                      setSearchResults([]);
                      setSearchBreadcrumbs([]);
                      await loadFolder(null);
                    } else {
                      await loadFolder(currentFolderId);
                    }
                  }
                }}
              >
                {i > 0 && <span style={{ color: '#bbb', fontWeight: 700, fontSize: 20, margin: '0 6px' }}>/</span>}
                <Folder size={22} style={{ marginRight: 6, color: '#2563eb' }} /> {b.name}
              </span>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
            {/* Szukajka */}
            <div style={{ position: 'relative', marginRight: 10, minWidth: 340, width: 340 }}>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={e => {
                  setSearchQuery(e.target.value);
                  setIsSearching(false);
                  setSearchResults([]);
                  setSearchBreadcrumbs([]);
                }}
                onFocus={() => { if (searchQuery.trim()) setShowSuggestions(true); }}
                onKeyDown={handleSearchInputKeyDown}
                placeholder="Szukaj dokumentów..."
                style={{
                  padding: '10px 38px 10px 16px',
                  borderRadius: 14,
                  border: '1.5px solid #e0e7ff',
                  fontSize: 16,
                  minWidth: 340,
                  width: 340,
                  background: '#fff',
                  boxShadow: '0 2px 8px #e0e7ff33',
                  outline: 'none',
                  transition: 'border 0.18s, box-shadow 0.18s',
                }}
                autoComplete="off"
              />
              <svg style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', width: 18, height: 18, color: '#2563eb', pointerEvents: 'none' }} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
              {showSuggestions && searchSuggestions.length > 0 && (
                <div style={{
                  position: 'absolute',
                  left: 0,
                  right: 'auto',
                  top: 44,
                  marginTop: 0,
                  zIndex: 1000,
                  background: '#fff',
                  border: '1.5px solid #e0e7ff',
                  borderRadius: 12,
                  boxShadow: '0 4px 24px #2563eb22',
                  maxHeight: 320,
                  overflowY: 'auto',
                  animation: 'fadeIn 0.18s',
                  width: 540,
                }}>
                  {searchSuggestions.map((doc, idx) => (
                    <div
                      key={doc.id}
                      style={{
                        padding: '12px 18px',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: 16,
                        color: '#2563eb',
                        borderBottom: '1px solid #e0e7ff',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        background: idx === selectedSuggestionIndex ? '#e0e7ff' : '#fff',
                        transition: 'background 0.15s',
                      }}
                      onMouseDown={() => handleSuggestionClick(doc)}
                      onMouseEnter={() => setSelectedSuggestionIndex(idx)}
                    >
                      <Download size={18} style={{ marginRight: 8 }} />
                      <span>{doc.title}</span>
                      <span style={{ color: '#888', fontWeight: 400, fontSize: 14, marginLeft: 10 }}>
                        {doc.createdAt ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                            <span style={{ color: '#666', fontWeight: 400, fontSize: 14 }}>{formatDateTime(doc.createdAt).label}</span>
                            <span style={{ color: '#888', marginLeft: 6, display: 'inline-flex', alignItems: 'center', fontWeight: 400, fontSize: 14 }}>
                              <Clock size={13} style={{ marginRight: 2, color: '#2563eb', verticalAlign: 'middle' }} />
                              {formatDateTime(doc.createdAt).time}
                            </span>
                          </span>
                        ) : ''}
                      </span>
                    </div>
                  ))}
                  <div
                    style={{
                      padding: '12px 18px',
                      cursor: 'pointer',
                      fontWeight: 700,
                      fontSize: 15,
                      color: '#2563eb',
                      background: '#f3f6fd',
                      textAlign: 'center',
                      borderRadius: '0 0 12px 12px',
                    }}
                    onMouseDown={handleShowAllResults}
                  >
                    Zobacz wszystkie wyniki
                  </div>
                </div>
              )}
            </div>
            <Button onClick={() => setIsAddFolderOpen(true)} className="add-file-btn enhanced-primary-btn" style={{ fontWeight: 700, fontSize: 18, borderRadius: 26, padding: '12px 36px', background: 'linear-gradient(90deg, #2563eb 0%, #60a5fa 100%)', color: '#fff', boxShadow: '0 4px 16px #2563eb33', letterSpacing: '0.5px', border: 'none', transition: 'box-shadow 0.18s, background 0.18s' }}>
              <FolderPlus size={22} style={{ marginRight: 10 }} /> Dodaj folder
            </Button>
          </div>
        </div>
      ) : (
        <div className="automation-header enhanced-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', background: 'linear-gradient(90deg, #e0e7ff 0%, #f8fafc 100%)', borderRadius: 18, boxShadow: '0 2px 16px rgba(37,99,235,0.07)', padding: '18px 32px 18px 24px', minHeight: 64 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 0, marginTop: 0 }}>
            {searchBreadcrumbs.map((b, i, arr) => (
              <span
                key={b.id || 'root'}
                style={{
                  color: i === arr.length - 1 ? '#2563eb' : '#888',
                  cursor: 'pointer',
                  fontWeight: i === arr.length - 1 ? 700 : 500,
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: 20,
                  background: dragOverFolderId === (b.id ?? 'root') ? '#e0e7ff' : 'transparent',
                  borderRadius: dragOverFolderId === (b.id ?? 'root') ? 10 : 0,
                  padding: dragOverFolderId === (b.id ?? 'root') ? '4px 12px' : 0,
                  transition: 'background 0.18s',
                  boxShadow: dragOverFolderId === (b.id ?? 'root') ? '0 2px 8px #c7d2fe' : 'none',
                }}
                onClick={() => (i !== arr.length - 1 ? handleBreadcrumbClick(b.id) : undefined)}
                onDragOver={e => { e.preventDefault(); setDragOverFolderId(b.id ?? 'root'); }}
                onDragLeave={e => { e.preventDefault(); setDragOverFolderId(null); }}
                onDrop={async e => {
                  e.preventDefault();
                  setDragOverFolderId(null);
                  if (draggedFileId && tenantId) {
                    await updateGeneratedDocumentFolder(tenantId, draggedFileId, b.id || null);
                    setDraggedFileId(null);
                    if ((b.id || null) === null) {
                      setIsSearching(false);
                      setSearchQuery("");
                      setSearchResults([]);
                      setSearchBreadcrumbs([]);
                      await loadFolder(null);
                    } else {
                      await loadFolder(currentFolderId);
                    }
                  }
                }}
              >
                {i > 0 && <span style={{ color: '#bbb', fontWeight: 700, fontSize: 20, margin: '0 6px' }}>/</span>}
                <Folder size={22} style={{ marginRight: 6, color: '#2563eb' }} /> {b.name}
              </span>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
            {/* Szukajka */}
            <div style={{ position: 'relative', marginRight: 10, minWidth: 340, width: 340 }}>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={e => {
                  setSearchQuery(e.target.value);
                  setIsSearching(false);
                  setSearchResults([]);
                  setSearchBreadcrumbs([]);
                }}
                onFocus={() => { if (searchQuery.trim()) setShowSuggestions(true); }}
                onKeyDown={handleSearchInputKeyDown}
                placeholder="Szukaj dokumentów..."
                style={{
                  padding: '10px 38px 10px 16px',
                  borderRadius: 14,
                  border: '1.5px solid #e0e7ff',
                  fontSize: 16,
                  minWidth: 340,
                  width: 340,
                  background: '#fff',
                  boxShadow: '0 2px 8px #e0e7ff33',
                  outline: 'none',
                  transition: 'border 0.18s, box-shadow 0.18s',
                }}
                autoComplete="off"
              />
              <svg style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', width: 18, height: 18, color: '#2563eb', pointerEvents: 'none' }} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
              {showSuggestions && searchSuggestions.length > 0 && (
                <div style={{
                  position: 'absolute',
                  left: 0,
                  right: 'auto',
                  top: 44,
                  marginTop: 0,
                  zIndex: 1000,
                  background: '#fff',
                  border: '1.5px solid #e0e7ff',
                  borderRadius: 12,
                  boxShadow: '0 4px 24px #2563eb22',
                  maxHeight: 320,
                  overflowY: 'auto',
                  animation: 'fadeIn 0.18s',
                  width: 540,
                }}>
                  {searchSuggestions.map((doc, idx) => (
                    <div
                      key={doc.id}
                      style={{
                        padding: '12px 18px',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: 16,
                        color: '#2563eb',
                        borderBottom: '1px solid #e0e7ff',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        background: idx === selectedSuggestionIndex ? '#e0e7ff' : '#fff',
                        transition: 'background 0.15s',
                      }}
                      onMouseDown={() => handleSuggestionClick(doc)}
                      onMouseEnter={() => setSelectedSuggestionIndex(idx)}
                    >
                      <Download size={18} style={{ marginRight: 8 }} />
                      <span>{doc.title}</span>
                      <span style={{ color: '#888', fontWeight: 400, fontSize: 14, marginLeft: 10 }}>
                        {doc.createdAt ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                            <span style={{ color: '#666', fontWeight: 400, fontSize: 14 }}>{formatDateTime(doc.createdAt).label}</span>
                            <span style={{ color: '#888', marginLeft: 6, display: 'inline-flex', alignItems: 'center', fontWeight: 400, fontSize: 14 }}>
                              <Clock size={13} style={{ marginRight: 2, color: '#2563eb', verticalAlign: 'middle' }} />
                              {formatDateTime(doc.createdAt).time}
                            </span>
                          </span>
                        ) : ''}
                      </span>
                    </div>
                  ))}
                  <div
                    style={{
                      padding: '12px 18px',
                      cursor: 'pointer',
                      fontWeight: 700,
                      fontSize: 15,
                      color: '#2563eb',
                      background: '#f3f6fd',
                      textAlign: 'center',
                      borderRadius: '0 0 12px 12px',
                    }}
                    onMouseDown={handleShowAllResults}
                  >
                    Zobacz wszystkie wyniki
                  </div>
                </div>
              )}
            </div>
            <Button onClick={() => setIsAddFolderOpen(true)} className="add-file-btn enhanced-primary-btn" style={{ fontWeight: 700, fontSize: 18, borderRadius: 26, padding: '12px 36px', background: 'linear-gradient(90deg, #2563eb 0%, #60a5fa 100%)', color: '#fff', boxShadow: '0 4px 16px #2563eb33', letterSpacing: '0.5px', border: 'none', transition: 'box-shadow 0.18s, background 0.18s' }}>
              <FolderPlus size={22} style={{ marginRight: 10 }} /> Dodaj folder
            </Button>
          </div>
        </div>
      )}

      {/* Tabela folderów i dokumentów */}
      <div className="table-container enhanced-table-container" style={{ position: 'relative', background: '#fff', borderRadius: 18, boxShadow: '0 4px 32px rgba(37,99,235,0.07)', padding: '0 0 18px 0', marginBottom: 24, overflowX: 'auto', overflowY: 'auto', paddingRight: 5, paddingLeft: 5, marginTop: 5  }}>
        <div style={{ height: 8 }} />
        <table className="file-table enhanced-table" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
          <thead>
            <tr className="enhanced-table-header-row" style={{ background: '#f3f6fd', borderBottom: '2.5px solid #e0e7ff', height: 56 }}>
              <th className="enhanced-table-th" style={{ width: '40%', minWidth: 180, color: '#222', fontWeight: 800, fontSize: 17, padding: '16px 0 16px 18px', letterSpacing: '0.02em', textAlign: 'left', textTransform: 'none', background: '#f3f6fd' }}>Nazwa</th>
              <th className="enhanced-table-th" style={{ width: '20%', minWidth: 120, color: '#222', fontWeight: 800, fontSize: 17, padding: '16px 0', textAlign: 'left', textTransform: 'none', background: '#f3f6fd' }}>Data modyfikacji</th>
              <th className="enhanced-table-th" style={{ width: 90, background: '#f3f6fd' }}></th>
            </tr>
          </thead>
          <tbody>
            {isFolderAndFilesLoading ? (
              <tr><td colSpan={3}><Loader /></td></tr>
            ) : isSearching && searchQuery.trim() ? (
              searchResults.length === 0 ? (
                <tr><td colSpan={3} style={{ textAlign: 'center', color: '#888', fontSize: 16, padding: 32 }}>Brak wyników.</td></tr>
              ) : (
                searchResults.map(file => (
                  <tr
                    key={file.id}
                    className="enhanced-table-row"
                    style={{ background: hoveredRowId === file.id ? '#f0f6ff' : '#fff', transition: 'background 0.18s', height: 28 }}
                    onMouseEnter={() => setHoveredRowId(file.id)}
                    onMouseLeave={() => setHoveredRowId(null)}
                    onDoubleClick={() => setPreviewDocument(file)}
                  >
                    <td className="enhanced-table-td" style={{ fontWeight: 700, paddingLeft: 18, color: '#18181b', background: '#fff', borderBottom: '1.5px solid #f1f5f9', borderRadius: '14px 0 0 14px' }}>
                      <span style={{ display: 'flex', alignItems: 'center' }}>
                        <FileText size={20} color="#2563eb" style={{ flexShrink: 0, marginRight: 8, filter: 'drop-shadow(0 2px 6px #c7d2fe)' }} />
                        <a href="#" style={{ color: '#18181b', fontWeight: 700, cursor: 'pointer', textDecoration: 'none' }}>{file.title}</a>
                      </span>
                    </td>
                    <td className="enhanced-table-td" style={{ color: '#18181b', background: '#fff', borderBottom: '1.5px solid #f1f5f9', fontSize: 15, fontWeight: 400, minWidth: 150, padding: '12px 0', letterSpacing: '0.01em' }}
                      title={formatDateTime(file.createdAt).full}
                    >
                      {file.createdAt ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                          <span style={{ color: '#666', fontWeight: 400, fontSize: 15 }}>{formatDateTime(file.createdAt).label}</span>
                          <span style={{ color: '#888', marginLeft: 8, display: 'inline-flex', alignItems: 'center', fontWeight: 400, fontSize: 15 }}>
                            <Clock size={14} style={{ marginRight: 3, color: '#2563eb', verticalAlign: 'middle' }} />
                            {formatDateTime(file.createdAt).time}
                          </span>
                        </span>
                      ) : '—'}
                    </td>
                    <td className="enhanced-table-td" style={{ textAlign: 'right', paddingRight: 24, background: '#fff', borderBottom: '1.5px solid #f1f5f9', borderRadius: '0 14px 14px 0', minWidth: 120}}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'flex-end' }}>
                        <button className="icon-btn download enhanced-ghost-btn" title="Pobierz dokument" style={{ background: '#f3f6fd', border: 'none', borderRadius: 10, padding: 7, alignItems: 'center', transition: 'background 0.18s, box-shadow 0.18s', boxShadow: '0 2px 8px #e0e7ff' }} onClick={() => downloadGeneratedDocument(tenantId, file.id, file.title)}>
                          <Download size={15} />
                        </button>
                        <button className="icon-btn edit enhanced-ghost-btn" title="Edytuj dokument" style={{ background: '#f3f6fd', border: 'none', borderRadius: 10, padding: 7, alignItems: 'center', transition: 'background 0.18s, box-shadow 0.18s', boxShadow: '0 2px 8px #e0e7ff' }} onClick={() => { setIsEditingDocument(file); setEditDocumentName(file.title); }}>
                          <Edit size={15} />
                        </button>
                        <button className="icon-btn delete enhanced-ghost-btn" title="Usuń dokument" style={{ background: '#f3f6fd', border: 'none', borderRadius: 10, padding: 7, alignItems: 'center', transition: 'background 0.18s, box-shadow 0.18s', boxShadow: '0 2px 8px #e0e7ff' }} onClick={() => setIsDeletingDocument(file)}>
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )
            ) : (
              <>
                {/* Foldery */}
                {sortedFolders.map(folder => (
                  <tr
                    key={folder.id}
                    className="enhanced-table-row"
                    style={{
                      cursor: 'pointer',
                      background: hoveredRowId === folder.id ? '#f0f6ff' : '#fff',
                      boxShadow: hoveredRowId === folder.id ? '0 2px 12px #c7d2fe44' : 'none',
                      transition: 'background 0.18s, box-shadow 0.18s',
                      height: 28
                    }}
                    onMouseEnter={() => setHoveredRowId(folder.id)}
                    onMouseLeave={() => setHoveredRowId(null)}
                    onDoubleClick={() => handleEnterFolder(folder.id!)}
                    onDragOver={e => { e.preventDefault(); setDragOverFolderId(folder.id!); }}
                    onDragLeave={e => { e.preventDefault(); setDragOverFolderId(null); }}
                    onDrop={async e => {
                      e.preventDefault();
                      setDragOverFolderId(null);
                      if (draggedFileId && tenantId) {
                        await updateGeneratedDocumentFolder(tenantId, draggedFileId, folder.id!);
                        setDraggedFileId(null);
                        if (folder.id === null) {
                          setIsSearching(false);
                          setSearchQuery("");
                          setSearchResults([]);
                          setSearchBreadcrumbs([]);
                          await loadFolder(null);
                        } else {
                          await loadFolder(currentFolderId);
                        }
                      }
                    }}
                  >
                    <td className="enhanced-table-td" style={{ fontWeight: 700, paddingLeft: 18, color: '#18181b', background: '#fff', borderBottom: '1.5px solid #f1f5f9', borderRadius: '14px 0 0 14px' }}>
                      <span style={{ display: 'flex', alignItems: 'center' }}>
                        <Folder size={20} color="#2563eb" style={{ flexShrink: 0, marginRight: 8, filter: 'drop-shadow(0 2px 6px #c7d2fe)' }} />
                        <span style={{ color: '#18181b', fontWeight: 700 }}>{folder.name}</span>
                      </span>
                    </td>
                    <td className="enhanced-table-td" style={{ color: '#18181b', background: '#fff', borderBottom: '1.5px solid #f1f5f9', fontSize: 15, fontWeight: 400, minWidth: 150, padding: '12px 0', letterSpacing: '0.01em' }}
                      title={folder.createdAt ? formatDateTime(folder.createdAt).full : ''}
                    >
                      {folder.createdAt ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                          <span style={{ color: '#666', fontWeight: 400, fontSize: 15 }}>{formatDateTime(folder.createdAt).label}</span>
                          <span style={{ color: '#888', marginLeft: 8, display: 'inline-flex', alignItems: 'center', fontWeight: 400, fontSize: 15 }}>
                            <Clock size={14} style={{ marginRight: 3, color: '#2563eb', verticalAlign: 'middle' }} />
                            {formatDateTime(folder.createdAt).time}
                          </span>
                        </span>
                      ) : '—'}
                    </td>
                    <td className="enhanced-table-td" style={{ textAlign: 'right', paddingRight: 24, background: '#fff', borderBottom: '1.5px solid #f1f5f9', borderRadius: '0 14px 14px 0', minWidth: 120 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'flex-end' }}>
                        <button className="icon-btn edit enhanced-ghost-btn" title="Edytuj folder" style={{ background: '#f3f6fd', border: 'none', borderRadius: 10, padding: 7, marginRight: 5, transition: 'background 0.18s, box-shadow 0.18s', boxShadow: '0 2px 8px #e0e7ff' }} onClick={e => { e.stopPropagation(); setIsEditingFolder(folder); setEditFolderName(folder.name || ''); }}><Edit size={15} /></button>
                        <button className="icon-btn delete enhanced-ghost-btn" title="Usuń folder" style={{ background: '#f3f6fd', border: 'none', borderRadius: 10, padding: 7, transition: 'background 0.18s, box-shadow 0.18s', boxShadow: '0 2px 8px #e0e7ff' }} onClick={e => { e.stopPropagation(); setIsDeletingFolder(folder.id!); }}><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {/* Dokumenty */}
                {sortedDocuments.length === 0 && sortedFolders.length === 0 ? (
                  <td colSpan={3} style={{ textAlign: 'center', color: '#888', fontSize: 16, padding:10 }}>Brak plików ani folderów.</td>
                ) : (
                  sortedDocuments.map(file => (
                    <tr
                      key={file.id}
                      className="enhanced-table-row"
                      style={{ background: '#fff', transition: 'background 0.18s', height: 28 }}
                      draggable
                      onMouseEnter={() => setHoveredRowId(file.id)}
                      onMouseLeave={() => setHoveredRowId(null)}
                      onDragStart={() => setDraggedFileId(file.id)}
                      onDragEnd={async () => {
                        setDraggedFileId(null);
                        await loadFolder(currentFolderId);
                      }}
                      onDoubleClick={() => setPreviewDocument(file)}
                    >
                      <td className="enhanced-table-td" style={{ fontWeight: 700, paddingLeft: 18, color: '#18181b', background: '#fff', borderBottom: '1.5px solid #f1f5f9', borderRadius: '14px 0 0 14px' }}>
                        <span style={{ display: 'flex', alignItems: 'center' }}>
                          <FileText size={20} color="#2563eb" style={{ flexShrink: 0, marginRight: 8, filter: 'drop-shadow(0 2px 6px #c7d2fe)' }} />
                          <a href="#" style={{ color: '#18181b', fontWeight: 700, cursor: 'pointer', textDecoration: 'none' }}>{file.title}</a>
                        </span>
                      </td>
                      <td className="enhanced-table-td" style={{ color: '#18181b', background: '#fff', borderBottom: '1.5px solid #f1f5f9', fontSize: 15, fontWeight: 400, minWidth: 150, padding: '12px 0', letterSpacing: '0.01em' }}
                        title={formatDateTime(file.createdAt).full}
                      >
                        {file.createdAt ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                            <span style={{ color: '#666', fontWeight: 400, fontSize: 15 }}>{formatDateTime(file.createdAt).label}</span>
                            <span style={{ color: '#888', marginLeft: 8, display: 'inline-flex', alignItems: 'center', fontWeight: 400, fontSize: 15 }}>
                              <Clock size={14} style={{ marginRight: 3, color: '#2563eb', verticalAlign: 'middle' }} />
                              {formatDateTime(file.createdAt).time}
                            </span>
                          </span>
                        ) : '—'}
                      </td>
                      <td className="enhanced-table-td" style={{ textAlign: 'right', paddingRight: 24, background: '#fff', borderBottom: '1.5px solid #f1f5f9', borderRadius: '0 14px 14px 0', minWidth: 120}}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'flex-end' }}>
                          <button className="icon-btn download enhanced-ghost-btn" title="Pobierz dokument" style={{ background: '#f3f6fd', border: 'none', borderRadius: 10, padding: 7, alignItems: 'center', transition: 'background 0.18s, box-shadow 0.18s', boxShadow: '0 2px 8px #e0e7ff' }} onClick={() => downloadGeneratedDocument(tenantId, file.id, file.title)}>
                            <Download size={15} />
                          </button>
                          <button className="icon-btn edit enhanced-ghost-btn" title="Edytuj dokument" style={{ background: '#f3f6fd', border: 'none', borderRadius: 10, padding: 7, alignItems: 'center', transition: 'background 0.18s, box-shadow 0.18s', boxShadow: '0 2px 8px #e0e7ff' }} onClick={() => { setIsEditingDocument(file); setEditDocumentName(file.title); }}>
                            <Edit size={15} />
                          </button>
                          <button className="icon-btn delete enhanced-ghost-btn" title="Usuń dokument" style={{ background: '#f3f6fd', border: 'none', borderRadius: 10, padding: 7, alignItems: 'center', transition: 'background 0.18s, box-shadow 0.18s', boxShadow: '0 2px 8px #e0e7ff' }} onClick={() => setIsDeletingDocument(file)}>
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </>
            )}
          </tbody>
        </table>
        {isFolderAndFilesLoading && (
          <div style={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(255,255,255,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10
          }}>
            <Loader />
          </div>
        )}
      </div>

      {/* Modale do folderów (dodawanie, edycja, usuwanie) analogicznie do AutomationList */}
      {isAddFolderOpen && (
        <div className="modal-overlay" onClick={() => setIsAddFolderOpen(false)}>
          <div className="modal-content upload-modal" style={{ minWidth: 350, maxWidth: 400, padding: '32px 24px', borderRadius: 16 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ fontSize: '1.18rem', fontWeight: 700, margin: 0 }}>Dodaj folder</h2>
              <button className="close-button" onClick={() => setIsAddFolderOpen(false)}><X size={20} /></button>
            </div>
            <input type="text" className="modal-input" placeholder="Nazwa folderu" value={newFolderName} onChange={e => setNewFolderName(e.target.value)} style={{ margin: '18px 0' }} />
            <div className="modal-footer" style={{ gap: 16, justifyContent: 'flex-end' }}>
              <Button className="outline-button" onClick={() => setIsAddFolderOpen(false)}>Anuluj</Button>
              <Button className="upload-button" disabled={!newFolderName} onClick={async () => {
                if (!tenantId || !newFolderName) return;
                try {
                  await handleAddFolder(newFolderName);
                  setNewFolderName("");
                  setIsAddFolderOpen(false);
                } catch {
                  setToast({ message: "Błąd dodawania folderu.", type: "error" });
                }
              }}>Dodaj</Button>
            </div>
          </div>
        </div>
      )}
      {isDeletingFolder && (
        <div className="modal-overlay" onClick={() => setIsDeletingFolder(null)}>
          <div className="modal-content upload-modal" style={{ minWidth: 350, maxWidth: 400, padding: '32px 24px', borderRadius: 16 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ fontSize: '1.18rem', fontWeight: 700, margin: 0 }}>Usuń folder</h2>
              <button className="close-button" onClick={() => setIsDeletingFolder(null)}><X size={20} /></button>
            </div>
            <div style={{ margin: '18px 0', fontSize: 16 }}>Czy na pewno chcesz usunąć ten folder? Wszystkie pliki w folderze zostaną usunięte.</div>
            <div className="modal-footer" style={{ gap: 16, justifyContent: 'flex-end' }}>
              <Button className="outline-button" onClick={() => setIsDeletingFolder(null)}>Anuluj</Button>
              <Button className="upload-button" onClick={async () => {
                if (!tenantId || !isDeletingFolder) return;
                try {
                  await handleDeleteFolder(isDeletingFolder);
                  setIsDeletingFolder(null);
                } catch {
                  setToast({ message: "Błąd usuwania folderu.", type: "error" });
                }
              }}>Usuń</Button>
            </div>
          </div>
        </div>
      )}
      {isEditingFolder && (
        <div className="modal-overlay" onClick={() => setIsEditingFolder(null)}>
          <div className="modal-content upload-modal" style={{ minWidth: 350, maxWidth: 400, padding: '32px 24px', borderRadius: 16 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ fontSize: '1.18rem', fontWeight: 700, margin: 0 }}>Zmień nazwę folderu</h2>
              <button className="close-button" onClick={() => setIsEditingFolder(null)}><X size={20} /></button>
            </div>
            <input type="text" className="modal-input" placeholder="Nazwa folderu" value={editFolderName} onChange={e => setEditFolderName(e.target.value)} style={{ margin: '18px 0' }} />
            {editFolderError && <div style={{ color: 'red', marginBottom: 8 }}>{editFolderError}</div>}
            <div className="modal-footer" style={{ gap: 16, justifyContent: 'flex-end' }}>
              <Button className="outline-button" onClick={() => setIsEditingFolder(null)}>Anuluj</Button>
              <Button className="upload-button" disabled={!editFolderName || isEditingFolderLoading} onClick={handleEditFolder}>{isEditingFolderLoading ? 'Zapisywanie...' : 'Zapisz'}</Button>
            </div>
          </div>
        </div>
      )}
      {isEditingDocument && (
        <div className="modal-overlay" onClick={() => setIsEditingDocument(null)}>
          <div className="modal-content upload-modal" style={{ minWidth: 350, maxWidth: 400, padding: '32px 24px', borderRadius: 16 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ fontSize: '1.18rem', fontWeight: 700, margin: 0 }}>Zmień nazwę dokumentu</h2>
              <button className="close-button" onClick={() => setIsEditingDocument(null)}><X size={20} /></button>
            </div>
            <input type="text" className="modal-input" placeholder="Nazwa dokumentu" value={editDocumentName} onChange={e => setEditDocumentName(e.target.value)} style={{ margin: '18px 0' }} />
            {editDocumentError && <div style={{ color: 'red', marginBottom: 8 }}>{editDocumentError}</div>}
            <div className="modal-footer" style={{ gap: 16, justifyContent: 'flex-end' }}>
              <Button className="outline-button" onClick={() => setIsEditingDocument(null)}>Anuluj</Button>
              <Button className="upload-button" disabled={!editDocumentName || isEditingDocumentLoading} onClick={handleEditDocument}>{isEditingDocumentLoading ? 'Zapisywanie...' : 'Zapisz'}</Button>
            </div>
          </div>
        </div>
      )}
      {isDeletingDocument && (
        <div className="modal-overlay" onClick={() => setIsDeletingDocument(null)}>
          <div className="modal-content upload-modal" style={{ minWidth: 350, maxWidth: 400, padding: '32px 24px', borderRadius: 16 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ fontSize: '1.18rem', fontWeight: 700, margin: 0 }}>Usuń dokument</h2>
              <button className="close-button" onClick={() => setIsDeletingDocument(null)}><X size={20} /></button>
            </div>
            <div style={{ margin: '18px 0', fontSize: 16 }}>Czy na pewno chcesz usunąć ten dokument?</div>
            <div className="modal-footer" style={{ gap: 16, justifyContent: 'flex-end' }}>
              <Button className="outline-button" onClick={() => setIsDeletingDocument(null)}>Anuluj</Button>
              <Button className="upload-button" onClick={handleDeleteDocument}>Usuń</Button>
            </div>
          </div>
        </div>
      )}
      {previewDocument && (
        <WordPreview
          tenantId={tenantId}
          documentId={previewDocument.id}
          title={previewDocument.title}
          onClose={() => setPreviewDocument(null)}
        />
      )}
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
      <style>{`
        .toast {
          position: fixed;
          right: 32px;
          bottom: 32px;
          min-width: 240px;
          max-width: 320px;
          background: #fff;
          color: #222;
          border-radius: 10px;
          box-shadow: 0 4px 24px rgba(0,0,0,0.13);
          padding: 16px 22px;
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 1rem;
          font-weight: 500;
          z-index: 999999;
          animation: toastIn 0.3s cubic-bezier(.4,2,.6,1);
        }
        .toast.success { border-left: 5px solid #22c55e; }
        .toast.error { border-left: 5px solid #ef4444; }
        @keyframes toastIn {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .pagination-circle, .pagination-arrow {
          width: 36px;
          height: 36px;
          border-radius: 50% !important;
          border: 2px solid #ccc;
          background: #fff;
          margin: 0 6px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: border 0.18s, color 0.18s, background 0.18s;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 0;
          box-sizing: border-box;
        }
        .pagination-circle.active {
          border: 2.5px solid #2563eb;
          color: #2563eb !important;
          background: #fff;
        }
        .pagination-circle:hover, .pagination-arrow:hover:not(:disabled) {
          border: 2px solid #2563eb;
          color: #2563eb;
          background: #f0f6ff;
        }
        .pagination-arrow:disabled {
          opacity: 0.4;
          cursor: not-allowed;
          border: 2px solid #eee;
          color: #bbb;
          background: #fff;
        }
        .enhanced-table-row:hover {
          background: #f0f6ff !important;
          box-shadow: 0 2px 12px #c7d2fe44 !important;
        }
        .enhanced-table-td:first-child {
          border-radius: 14px 0 0 14px;
        }
        .enhanced-table-td:last-child {
          border-radius: 0 14px 14px 0;
        }
        .gmail-overlay { cursor: pointer; }
        .gmail-doc-bg { cursor: default; }
        @media (max-width: 900px) {
          .gmail-doc-bg {
            max-width: 100vw !important;
            width: 100vw !important;
            margin: 56px 0 0 0 !important;
          }
        }
      `}</style>
    </>
  );
}